import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { Download, X } from "lucide-react";
import LexiTipsLogo from "@/assets/lexi-tips-logo.svg";
import AppleTemplateComponent from "./AppleTemplate";
import logger from "@/utils/logger";
import { takeScreenshot as apiTakeScreenshot } from "../api/vercel";

function WinningTipModal({
  tip,
  isOpen,
  onClose,
  onImageGenerated,
  historicalData,
  selectedDate,
}) {
  const [logoLoaded, setLogoLoaded] = useState(false);
  const logoRef = useRef(null);
  const [isScreenshotLoading, setIsScreenshotLoading] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState("");

  // Default logo URL as fallback
  const defaultLogoUrl = LexiTipsLogo;

  // Ensure logo is loaded before allowing download
  useEffect(() => {
    if (isOpen && logoRef.current) {
      // If the logo is already loaded, set state to true
      if (logoRef.current.complete) {
        setLogoLoaded(true);
      } else {
        // If not complete, add a load listener
        const handleLoad = () => {
          setLogoLoaded(true);
        };
        logoRef.current.addEventListener("load", handleLoad);
        return () => {
          if (logoRef.current) {
            logoRef.current.removeEventListener("load", handleLoad);
          }
        };
      }
    }
  }, [isOpen, screenshotUrl]); // Re-run when screenshotUrl changes

  // Automatically take screenshot when modal opens
  useEffect(() => {
    if (isOpen && tip) {
      takeScreenshot();
    }
  }, [isOpen, tip]);

  // Function to take a screenshot using screenshotapi.net
  const takeScreenshot = async () => {
    // Don't retake screenshot if we already have one
    if (screenshotUrl) return;

    setIsScreenshotLoading(true);

    try {
      const response = await apiTakeScreenshot({
        trackName: tip.trackName,
        raceNumber: tip.raceNumber,
      });

      if (response.success && response.screenshot) {
        logger.log("Screenshot URL:", response.screenshot);
        setScreenshotUrl(response.screenshot);
        setLogoLoaded(false); // Reset logo loaded flag to ensure new image loads properly
      }
    } catch (error) {
      logger.error("Error taking screenshot:", error);
      alert("Failed to take screenshot. Please check console for details.");
    } finally {
      setIsScreenshotLoading(false);
    }
  };

  // Handle image download
  const handleImageDownload = (event) => {
    if (event) event.stopPropagation();
    const modalContent = document.getElementById("image-modal-content");

    if (!modalContent) return;

    // Make sure logo is loaded
    if (!logoLoaded) {
      alert("Please wait for the logo to load...");
      return;
    }

    // Get UI controls that should be hidden during capture
    const closeButton = document.getElementById("modal-close-btn");
    const downloadButton = document.getElementById("modal-download-btn");

    // Hide the buttons during capture
    if (closeButton) closeButton.style.display = "none";
    if (downloadButton) downloadButton.style.display = "none";

    // Capture at higher quality
    html2canvas(modalContent, {
      backgroundColor: "#FCEDF7",
      scale: 10, // Increased from 3 for better quality
      logging: false,
      allowTaint: true,
      useCORS: true,
      imageTimeout: 0, // Wait for images indefinitely
      onclone: function (clonedDoc) {
        // Get the logo in the cloned document
        const clonedLogo = clonedDoc.querySelector("#lexi-logo");
        if (clonedLogo) {
          // Ensure the cloned logo has loaded
          clonedLogo.setAttribute("crossorigin", "anonymous");
          clonedLogo.style.visibility = "visible";
        }
      },
    })
      .then((canvas) => {
        // Restore buttons after capture
        if (closeButton) closeButton.style.display = "flex";
        if (downloadButton) downloadButton.style.display = "flex";

        // Create a new canvas of exactly 1080x1080px
        const finalCanvas = document.createElement("canvas");
        finalCanvas.width = 1080;
        finalCanvas.height = 1080;
        const ctx = finalCanvas.getContext("2d");

        // Fill with background color
        ctx.fillStyle = "#FCEDF7";
        ctx.fillRect(0, 0, 1080, 1080);

        // Calculate scaling to maintain proportions
        const scale = Math.min(1080 / canvas.width, 1080 / canvas.height);

        // Draw the captured content centered
        const x = (1080 - canvas.width * scale) / 2;
        const y = (1080 - canvas.height * scale) / 2;

        // Draw with proper scaling
        ctx.drawImage(
          canvas,
          0,
          0,
          canvas.width,
          canvas.height,
          x,
          y,
          canvas.width * scale,
          canvas.height * scale,
        );

        // Now manually add the logo if needed
        const logo = new window.Image();
        logo.crossOrigin = "anonymous";
        logo.src = LexiTipsLogo;
        logo.onload = function () {
          // Draw logo in the bottom right - make it bigger than the original w-36 h-12
          const logoWidth = 340; // Increased from 200px
          const logoHeight = 120; // Increased from 70px
          ctx.drawImage(
            logo,
            1080 - logoWidth - 95,
            1080 - logoHeight - 40,
            logoWidth,
            logoHeight,
          );

          // Now save with logo included
          finalCanvas.toBlob(
            function (blob) {
              // Create download link
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");

              link.download = `lexi-winner-${tip.trackName.replace(/\s+/g, "-").toLowerCase()}-r${tip.raceNumber}-${tip.horseName.replace(/\s+/g, "-").toLowerCase()}.jpg`;
              link.href = url;
              link.click();

              // Clean up
              URL.revokeObjectURL(url);

              // Notify parent component that image was generated (optional)
              if (onImageGenerated) {
                onImageGenerated(url);
              }
            },
            "image/jpeg",
            1.0, // Maximum quality
          );
        };

        logo.onerror = function () {
          logger.error("Failed to load logo for final canvas");
          // Still save the image without the logo as a fallback
          finalCanvas.toBlob(
            function (blob) {
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");

              link.download = `lexi-winner-${tip.trackName.replace(/\s+/g, "-").toLowerCase()}-r${tip.raceNumber}-${tip.horseName.replace(/\s+/g, "-").toLowerCase()}.jpg`;
              link.href = url;
              link.click();

              URL.revokeObjectURL(url);
            },
            "image/jpeg",
            1.0, // Maximum quality
          );
        };
      })
      .catch((err) => {
        // Restore buttons on error
        if (closeButton) closeButton.style.display = "flex";
        if (downloadButton) downloadButton.style.display = "flex";

        logger.error("Error generating image:", err);
        alert("Sorry, there was an error creating your image.");
      });
  };

  if (!isOpen || !tip) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="relative overflow-hidden w-full max-w-md aspect-square bg-[#FCEDF7] flex flex-col items-center justify-center m-4 p-6 text-center"
        onClick={(e) => e.stopPropagation()}
        id="image-modal-content"
      >
        {/* Close button */}
        <button
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black bg-opacity-10 hover:bg-opacity-20 flex items-center justify-center"
          onClick={onClose}
          id="modal-close-btn"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Download button */}
        <button
          className="absolute top-2 left-2 flex items-center justify-center gap-1 px-3 py-1.5 rounded-full bg-black bg-opacity-10 hover:bg-opacity-20 text-sm font-medium"
          onClick={handleImageDownload}
          data-gtm-element-id="winning-tips_modal_download"
          id="modal-download-btn"
          disabled={!logoLoaded || isScreenshotLoading}
        >
          <Download className="h-4 w-4" />
          {isScreenshotLoading ? "Loading..." : "Download"}
        </button>

        {/* Winner announcement */}
        <h2
          className="text-brand-pink text-[2rem] font-black mb-2 italic"
          style={{
            position: "absolute",
            fontSize: "clamp(28px, 7.5vw, 81px)",
            top: "21%",
            // color: "green",
          }}
        >
          ANOTHER WINNER!
        </h2>

        {/* Horse details */}
        <h3
          className="text-2xl font-black text-black italic"
          style={{
            position: "absolute",
            fontSize: "clamp(19.1px, 6vw, 69px)",
            top: "36.3%",
            // color: "green",
          }}
        >
          <span className="text-xl">#{tip.horseNumber}</span>{" "}
          {tip.horseName.toUpperCase()}
        </h3>
        <p className="text-base text-black mt-1 font-bold italic mb-[15px]">
          {tip.trackName} R{tip.raceNumber}
        </p>

        {
          !isScreenshotLoading && <AppleTemplateComponent
            screenshotUrl={screenshotUrl}
            tip={{
              ...tip,
              tipUpdatedAt: tip.tipUpdatedAt || "12:00",
              resultedAt: tip.resultedAt || "10:30",
              silks: tip.silks || null,
            }}
            firstFourData={
              tip.firstFourData || [
                {
                  horseNumber: tip.horseNumber,
                  horseName: tip.horseName,
                  silks: tip.silks || null,
                },
                tip.secondPick || {
                  horseNumber: (parseInt(tip.horseNumber) + 1).toString(),
                  horseName: "Second Pick",
                  silks: null,
                },
              ]
            }
            rankingChanges={
              tip.rankingChanges || {
                [tip.horseNumber]: "up", // Winner typically moved up
                [tip.secondPick?.horseNumber ||
                  (parseInt(tip.horseNumber) + 1).toString()]: "same", // Second pick stayed same
              }
            }
            historicalData={historicalData}
            selectedDate={selectedDate}
          />
        }

        {/* Branding */}
        <div className="absolute bottom-6 right-5 text-xl font-bold italic">
          <img
            src={LexiTipsLogo}
            alt="Lexi.tips"
            className="w-36 h-12"
            ref={logoRef}
            id="lexi-logo"
            crossOrigin="anonymous"
          />
        </div>

        {/* Loading indicator when screenshot is being generated */}
        {isScreenshotLoading && (
          <div className="absolute inset-0 bg-[#FCEDF7] flex items-center justify-center">
            <p className="text-lg font-semibold">Loading screenshot...</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default WinningTipModal;
